import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Segmented,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import uPlot, { type AlignedData, type Options } from "uplot";
import "uplot/dist/uPlot.min.css";
import {
  api,
  type IPNode,
  type IPProtoSeriesResp,
  type ProtoPoint,
} from "../api";
import { useGeoSettings } from "../contexts/GeoSettingsContext";
import { useIPZone } from "../contexts/IPZoneContext";
import { useRefreshAll, REFRESH_ALL_EVENT } from "../hooks/useRefreshAll";

const { Text } = Typography;

// ── format helpers ────────────────────────────────────────────────────────────

// Full format for legend / tooltip (hover values)
function fmtBps(v: number | null): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(2) + " Gbps";
  if (a >= 1e6) return (v / 1e6).toFixed(2) + " Mbps";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kbps";
  return Math.round(v) + " bps";
}

// Short format for Y-axis tick labels — prevents clipping in narrow axis.
function fmtBpsTick(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return (a / 1e9).toFixed(1) + " G";
  if (a >= 1e6) return (a / 1e6).toFixed(0) + " M";
  if (a >= 1e3) return (a / 1e3).toFixed(0) + " k";
  return Math.round(a) + "";
}

const CUSTOM_RANGE_VALUE = -1;

const RANGES = [
  { label: "Last 5 min",    value: 5 * 60 },
  { label: "Last 10 min",   value: 10 * 60 },
  { label: "Last 15 min",   value: 15 * 60 },
  { label: "Last Hour",     value: 3600 },
  { label: "Last 3 Hours",  value: 3 * 3600 },
  { label: "Last 12 Hours", value: 12 * 3600 },
  { label: "Last Day",      value: 24 * 3600 },
  { label: "Last Week",     value: 7 * 24 * 3600 },
  { label: "Custom range…", value: CUSTOM_RANGE_VALUE },
];

const REFRESHES = [
  { label: "off",        value: 0 },
  { label: "5 seconds",  value: 5_000 },
  { label: "30 seconds", value: 30_000 },
  { label: "1 minute",   value: 60_000 },
  { label: "5 minutes",  value: 5 * 60_000 },
];

type ViewMode = "total" | "nix" | "ixp";

const ALL_VIEW_OPTIONS = [
  { label: "Total",  value: "total" as ViewMode },
  { label: "NIX",   value: "nix"   as ViewMode },
  { label: "IXP",   value: "ixp"   as ViewMode },
];

// ── protocol color map ────────────────────────────────────────────────────────

// Decoders the chart is allowed to render, in Wansight legend order. Sensors
// may emit more (e.g. CHARGEN, SSDP, LDAP) but those go to the per-decoder
// reflection widgets — keeping the proto chart focused on the families a
// network operator wants on a single screen.
const DISPLAY_DECODERS = [
  "IP", "TCP", "TCP+SYN", "UDP", "ICMP", "OTHER",
  "HTTP", "HTTPS", "DNS", "MAIL", "SSH",
  "NTP", "SNMP", "RDP", "SIP",
  "QUIC", "GRE", "IPSEC", "NETBIOS",
  "FLOW+SYN", "TCP-NULL", "TCP+RST", "TCP+ACK", "TCP+SYNACK", "TCP-ALL",
  "INVALID",
] as const;

// Colours mirror Wansight's `protocols.graph_color_in` so charts compare 1:1.
const PROTO_COLORS: Record<string, string> = {
  "IP":         "#0AA130", // tokens-allow: data
  "TCP":        "#6666FF", // tokens-allow: data
  "TCP+SYN":    "#FF4444", // tokens-allow: data
  "UDP":        "#FF9525", // tokens-allow: data
  "ICMP":       "#669900", // tokens-allow: data
  "OTHER":      "#585858", // tokens-allow: data
  "INVALID":    "#000000", // tokens-allow: data
  "HTTP":       "#74B3FF", // tokens-allow: data
  "HTTPS":      "#3330FF", // tokens-allow: data
  "MAIL":       "#4590B6", // tokens-allow: data
  "DNS":        "#FFC93F", // tokens-allow: data
  "IPSEC":      "#989898", // tokens-allow: data
  "SSH":        "#00C0C0", // tokens-allow: data
  "SIP":        "#F0C0C0", // tokens-allow: data
  "NTP":        "#98FF98", // tokens-allow: data
  "SNMP":       "#FFB570", // tokens-allow: data
  "RDP":        "#00417F", // tokens-allow: data
  "FLOW+SYN":   "#FF4444", // tokens-allow: data
  "TCP-NULL":   "#FF0000", // tokens-allow: data
  "TCP+RST":    "#D49B9B", // tokens-allow: data
  "SSDP":       "#7F5024", // tokens-allow: data
  "TCP+ACK":    "#736C6C", // tokens-allow: data
  "TCP+SYNACK": "#783E3E", // tokens-allow: data
  "TCP-ALL":    "#58591E", // tokens-allow: data
  "NETBIOS":    "#9D0C0C", // tokens-allow: data
  "QUIC":       "#6D2411", // tokens-allow: data
  "MEMCACHED":  "#0FFB84", // tokens-allow: data
  "LDAP":       "#A9D3BE", // tokens-allow: data
  "CLDAP":      "#76A18B", // tokens-allow: data
  "CHARGEN":    "#731363", // tokens-allow: data
  "SLP":        "#236744", // tokens-allow: data
  "GRE":        "#244A6F", // tokens-allow: data
};

// ── legend format helpers ─────────────────────────────────────────────────────

function fmtBpsLegend(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(1) + " Gbits/s";
  if (a >= 1e6) return (v / 1e6).toFixed(1) + " Mbits/s";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kbits/s";
  return v.toFixed(1) + " bits/s";
}

function fmtBitsSum(bits: number): string {
  const a = Math.abs(bits);
  if (a >= 1e9) return (bits / 1e9).toFixed(1) + " Gbits";
  if (a >= 1e6) return (bits / 1e6).toFixed(1) + " Mbits";
  if (a >= 1e3) return (bits / 1e3).toFixed(1) + " kbits";
  return bits.toFixed(1) + " bits";
}

// PPS formatters
function fmtPps(v: number | null): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(2) + " Mpps";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return Math.round(v) + " pps";
}
function fmtPpsLegend(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(2) + " Mpps";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return v.toFixed(1) + " pps";
}
function fmtPpsSum(pkts: number): string {
  const a = Math.abs(pkts);
  if (a >= 1e9) return (pkts / 1e9).toFixed(1) + " Gpkts";
  if (a >= 1e6) return (pkts / 1e6).toFixed(1) + " Mpkts";
  if (a >= 1e3) return (pkts / 1e3).toFixed(1) + " kpkts";
  return Math.round(pkts) + " pkts";
}
function fmtPpsTick(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e6) return (a / 1e6).toFixed(1) + " M";
  if (a >= 1e3) return (a / 1e3).toFixed(0) + " k";
  return Math.round(a) + "";
}

type MetricMode = "bps" | "pps";

// ── ProtoLegend table ─────────────────────────────────────────────────────────

function ProtoLegend({
  decoders, points, metric,
}: { decoders: string[]; points: ProtoPoint[]; metric: MetricMode }) {
  const totalSecs = points.length > 1 ? points[points.length - 1].t - points[0].t : 0;
  const isPPS = metric === "pps";

  const stats = decoders.map((dec) => {
    const inKey  = isPPS ? "in_pps"  : "in";
    const outKey = isPPS ? "out_pps" : "out";
    const inVals  = points.map((p) => (p[inKey]  as Record<string, number>)[dec]  ?? 0);
    const outVals = points.map((p) => (p[outKey] as Record<string, number>)[dec] ?? 0);
    const inMax   = Math.max(...inVals);
    const inMin   = Math.min(...inVals);
    const inAvg   = inVals.reduce((a, b) => a + b, 0) / (inVals.length || 1);
    const outMax  = Math.max(...outVals);
    const outMin  = Math.min(...outVals);
    const outAvg  = outVals.reduce((a, b) => a + b, 0) / (outVals.length || 1);
    return {
      dec,
      inMax, inMin, inAvg, inSum: inAvg * totalSecs,
      outMax, outMin, outAvg, outSum: outAvg * totalSecs,
    };
  });

  const fmtVal = isPPS ? fmtPpsLegend : fmtBpsLegend;
  const fmtSum = isPPS ? fmtPpsSum    : fmtBitsSum;

  const cell: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 12, minWidth: 150, display: "inline-block",
  };
  const nameCell: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 12, width: 72, display: "inline-block",
  };
  const sumCell: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 12, minWidth: 130, display: "inline-block",
  };
  const headerStyle: React.CSSProperties = {
    fontWeight: "bold", fontSize: 12, padding: "5px 0 2px", fontFamily: "sans-serif",
  };

  const renderSection = (label: string, dir: "in" | "out") => (
    <div>
      <div style={headerStyle}>{label}</div>
      {stats.map((s) => {
        const color  = PROTO_COLORS[s.dec] ?? "#999"; // tokens-allow: data fallback
        const max    = dir === "in" ? s.inMax  : s.outMax;
        const min    = dir === "in" ? s.inMin  : s.outMin;
        const avg    = dir === "in" ? s.inAvg  : s.outAvg;
        const sum    = dir === "in" ? s.inSum  : s.outSum;
        return (
          <div key={s.dec} style={{ display: "flex", alignItems: "center", lineHeight: "18px" }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: color, marginRight: 4, flexShrink: 0 }} />
            <span style={nameCell}>{s.dec}</span>
            <span style={cell}>Max = {fmtVal(max)}</span>
            <span style={cell}>Avg = {fmtVal(avg)}</span>
            <span style={cell}>Min = {fmtVal(min)}</span>
            <span style={sumCell}>Sum = {fmtSum(sum)}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ marginTop: 10, overflowX: "auto" }}>
      {renderSection("INBOUND:", "in")}
      <div style={{ marginTop: 6 }}>
        {renderSection("OUTBOUND:", "out")}
      </div>
    </div>
  );
}

// ── ProtoChart (zone node / individual host / CIDR range) ────────────────────

interface ProtoChartProps {
  nodeID?: number;   // exact zone node → ipNodeProtoSeries / ipNodeGeoProtoSeries
  addr?: string;     // individual IP   → ipHostProtoSeries / ipHostGeoProtoSeries
  prefix?: string;   // CIDR range      → ipCIDRProtoSeries / ipCIDRGeoProtoSeries
  zoneID?: number;   // whole zone      → zoneProtoSeries (geoClass-aware)
  seconds: number;
  fromTs?: number;   // absolute range — overrides seconds when set
  toTs?: number;
  nonce: number;
  geoClass?: "nix" | "ixp"; // when set, fetches from geo-proto endpoints
}

function ProtoChart({
  nodeID,
  addr,
  prefix,
  zoneID,
  seconds,
  fromTs,
  toTs,
  nonce,
  geoClass,
}: ProtoChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  // Track which metric and container the current chart was built with so we
  // only rebuild when something structurally changes, not on every data poll.
  const lastMetricRef = useRef<MetricMode | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [protoData, setProtoData] = useState<IPProtoSeriesResp | null>(null);
  // initialLoading is true only until the first fetch completes. Subsequent
  // polls (nonce bumps) keep protoData alive and don't show a spinner — this
  // prevents the chart div from being unmounted/remounted on every refresh.
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState<MetricMode>("bps");

  useEffect(() => {
    let cancelled = false;
    setError("");

    // zone-aggregate mode honours geoClass: ?class=nix|ixp routes the backend
    // to ip_geo_proto so the NIX/IXP toggle actually changes the zone chart.
    const fetchFn = zoneID != null
      ? api.zoneProtoSeries(zoneID, seconds, geoClass, fromTs, toTs)
      : geoClass != null
      ? (prefix != null
          ? api.ipCIDRGeoProtoSeries(prefix, seconds, geoClass, fromTs, toTs)
          : addr != null
            ? api.ipHostGeoProtoSeries(addr, seconds, geoClass, fromTs, toTs)
            : api.ipNodeGeoProtoSeries(nodeID!, seconds, geoClass, fromTs, toTs))
      : (prefix != null
          ? api.ipCIDRProtoSeries(prefix, seconds, fromTs, toTs)
          : addr != null
            ? api.ipHostProtoSeries(addr, seconds, fromTs, toTs)
            : api.ipNodeProtoSeries(nodeID!, seconds, fromTs, toTs));

    fetchFn
      .then((d) => {
        if (cancelled) return;
        // Drop trailing all-zero points (NetFlow late-fragment lookback
        // window) so charts in shorter time ranges don't render blank.
        // Capped at 12 bins (60 s) so a real outage stays visible.
        const pts = d.points ?? [];
        let trimAt = pts.length;
        const MAX_TRIM = 12;
        for (let i = pts.length - 1; i >= 0 && pts.length - trimAt < MAX_TRIM; i--) {
          const p = pts[i];
          const inSum = Object.values(p.in ?? {}).reduce((a, b) => a + b, 0);
          const outSum = Object.values(p.out ?? {}).reduce((a, b) => a + b, 0);
          if (inSum > 0 || outSum > 0) break;
          trimAt = i;
        }
        const trimmed = trimAt === pts.length ? d : { ...d, points: pts.slice(0, trimAt) };
        setProtoData(trimmed);
        setInitialLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setInitialLoading(false);
      });

    return () => { cancelled = true; };
  }, [nodeID, addr, prefix, zoneID, seconds, fromTs, toTs, nonce, geoClass]);

  // Rebuild chart whenever data or metric mode changes.
  useEffect(() => {
    if (!protoData || !ref.current) return;

    const isPPS = metric === "pps";
    const inKey  = isPPS ? "in_pps"  : "in"  as const;
    const outKey = isPPS ? "out_pps" : "out" as const;
    const fmtLive = isPPS ? fmtPps : fmtBps;
    const fmtTick = isPPS ? fmtPpsTick : fmtBpsTick;

    // Filter to only the 7 display decoders, in canonical order.
    // Guard against null: backend returns null (not []) when InfluxDB has no rows yet.
    const decoders = (protoData.decoders ?? []).filter((d) => (DISPLAY_DECODERS as readonly string[]).includes(d));
    const { points } = protoData;
    const ts = points.map((p) => p.t);

    // Build aligned data: [ts, dec0_in, dec0_out_neg, dec1_in, dec1_out_neg, ...]
    // Guard against missing in_pps/out_pps keys (omitempty on backend can drop them
    // when one direction has no traffic for a given time bucket).
    const buildAligned = (): AlignedData => {
      const aligned: AlignedData = [ts];
      for (const dec of decoders) {
        aligned.push(points.map((p) => ((p[inKey]  as Record<string, number> | undefined) ?? {})[dec] ?? null));
        aligned.push(points.map((p) => {
          const v = ((p[outKey] as Record<string, number> | undefined) ?? {})[dec];
          return v ? -v : null;
        }));
      }
      return aligned;
    };

    const alignedData = buildAligned();

    // If the chart already exists, is attached to the CURRENT container, has
    // the same decoder set, and the same metric, update data in-place without
    // destroying/rebuilding — so the chart never flashes on refresh.
    // chartContainerRef guards against the case where the container div was
    // unmounted and remounted (ref.current changed) while plotRef still points
    // to the old, now-detached uPlot instance.
    if (
      plotRef.current &&
      chartContainerRef.current === ref.current &&
      plotRef.current.series.length === decoders.length * 2 + 1 &&
      lastMetricRef.current === metric
    ) {
      plotRef.current.setData(alignedData);
      return;
    }

    // Metric, decoder set, or container changed — need a full rebuild.
    if (plotRef.current) {
      plotRef.current.destroy();
      plotRef.current = null;
    }
    lastMetricRef.current = metric;

    const seriesDefs = decoders.flatMap((dec) => {
      const color = PROTO_COLORS[dec] ?? "#999"; // tokens-allow: data fallback
      return [
        {
          label: `${dec} in`,
          stroke: color,
          fill: color + "bb",
          width: 1,
          value: (_u: uPlot, v: number | null) => fmtLive(v),
        },
        {
          label: `${dec} out`,
          stroke: color,
          fill: color + "bb",
          width: 1,
          value: (_u: uPlot, v: number | null) => fmtLive(v == null ? null : -v),
        },
      ];
    });

    const opts: Options = {
      width: ref.current.clientWidth,
      height: 280,
      cursor: { drag: { x: false, y: false } },
      legend: { show: false },
      scales: { x: { time: true }, y: { auto: true } },
      axes: [
        { stroke: "rgba(148,161,196,0.55)", grid: { stroke: "rgba(255,255,255,0.06)", width: 1 }, font: "11px sans-serif" },
        {
          stroke: "rgba(148,161,196,0.55)",
          grid: { stroke: "rgba(255,255,255,0.06)", width: 1 },
          font: "11px sans-serif",
          size: 52,
          gap: 6,
          values: (_u, ticks) => ticks.map(fmtTick),
        },
      ],
      series: [{}, ...seriesDefs],
    };

    chartContainerRef.current = ref.current;
    plotRef.current = new uPlot(opts, alignedData, ref.current);

    const chartEl = ref.current;
    const ro = new ResizeObserver(() => {
      if (plotRef.current && chartEl)
        plotRef.current.setSize({ width: chartEl.clientWidth, height: 280 });
    });
    ro.observe(chartEl);
    // Only disconnect the ResizeObserver on cleanup — do NOT destroy the chart
    // here. The chart is destroyed explicitly at the top of the next effect run
    // (when rebuild is needed), or on unmount via the effect below.
    // Destroying the chart in this cleanup would null out plotRef.current before
    // the next run's in-place check fires, forcing a full rebuild on every poll.
    return () => { ro.disconnect(); };
  }, [protoData, metric]);

  // Destroy the uPlot chart only when this component unmounts.
  useEffect(() => {
    return () => {
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, []);

  if (initialLoading) return <Spin style={{ display: "block", padding: 40 }} />;
  if (error)   return <Alert type="error" message={error} />;

  const displayDecoders = (protoData?.decoders ?? []).filter((d) => (DISPLAY_DECODERS as readonly string[]).includes(d));
  const hasData = protoData && protoData.points.length > 0 && displayDecoders.length > 0;

  // No data: render an explicit-height placeholder so the card isn't blank.
  // Do NOT create a uPlot chart — avoids calling .filter() on a null decoders
  // value and keeps the component lightweight.
  if (!hasData) {
    return (
      <div style={{ height: 300, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--muted)", fontSize: 13, gap: 8 }}>
        <InboxOutlined style={{ fontSize: 32 }} />
        <span>No data in this time range</span>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>
          Traffic will appear here once the sensor starts receiving flows.
        </span>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* BPS / PPS toggle */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <Segmented
          size="small"
          value={metric}
          onChange={(v) => setMetric(v as MetricMode)}
          options={[
            { label: "bps", value: "bps" },
            { label: "pps", value: "pps" },
          ]}
        />
      </div>
      <div ref={ref} style={{ width: "100%" }} />
      <ProtoLegend decoders={displayDecoders} points={protoData!.points} metric={metric} />
    </div>
  );
}

// ── NodeChart dispatcher ──────────────────────────────────────────────────────

function NodeChart({
  nodeID,
  addr,
  match,
  seconds,
  fromTs,
  toTs,
  nonce,
  view,
}: {
  nodeID?: number;
  addr?: string;
  match?: string;
  seconds: number;
  fromTs?: number;
  toTs?: number;
  nonce: number;
  view: ViewMode;
}) {
  const geoClass = (view === "nix" || view === "ixp") ? view : undefined;

  if (match === "zone") {
    return <ProtoChart zoneID={nodeID} seconds={seconds} fromTs={fromTs} toTs={toTs} nonce={nonce} geoClass={geoClass} />;
  }
  if (match === "host") {
    return <ProtoChart addr={addr} seconds={seconds} fromTs={fromTs} toTs={toTs} nonce={nonce} geoClass={geoClass} />;
  }
  if (match === "range") {
    return <ProtoChart prefix={addr} seconds={seconds} fromTs={fromTs} toTs={toTs} nonce={nonce} geoClass={geoClass} />;
  }
  return <ProtoChart nodeID={nodeID} seconds={seconds} fromTs={fromTs} toTs={toTs} nonce={nonce} geoClass={geoClass} />;
}

// ── result card ───────────────────────────────────────────────────────────────

function NodeCard({ node, seconds, fromTs, toTs, nonce, view }: { node: IPNode; seconds: number; fromTs?: number; toTs?: number; nonce: number; view: ViewMode }) {
  const matchColor: Record<string, string> = {
    host: "purple", range: "blue", exact: "green", parent: "geekblue", child: "orange",
    zone: "magenta",
  };
  const matchLabel: Record<string, string> = {
    host: "individual IP", range: "CIDR aggregate", exact: "exact match",
    parent: "parent subnet", child: "child subnet",
    zone: "whole IP zone",
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <span style={{ fontFamily: "monospace", fontSize: 14 }}>
          {node.prefix}
          {node.description && node.match !== "host" && node.match !== "range" && (
            <Text type="secondary" style={{ marginLeft: 10, fontSize: 12, fontFamily: "sans-serif" }}>
              {node.description}
            </Text>
          )}
        </span>
      }
      extra={<Tag color={matchColor[node.match] ?? "default"}>{matchLabel[node.match] ?? node.match}</Tag>}
    >
      <NodeChart nodeID={node.id || undefined} addr={node.addr} match={node.match} seconds={seconds} fromTs={fromTs} toTs={toTs} nonce={nonce} view={view} />
    </Card>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function IPAddresses() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [input, setInput] = useState("");
  const [nodes, setNodes] = useState<IPNode[]>([]);
  const [seconds, setSeconds] = useState(600);
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const fromTs = customRange ? customRange[0].unix() : undefined;
  const toTs   = customRange ? customRange[1].unix() : undefined;
  const [refreshMs, setRefreshMs] = useState(0);
  const [nonce, setNonce] = useState(0);

  // Initialize seconds + refreshMs from Settings so this page honours the
  // same defaults as the dashboard without the user having to change them
  // manually every visit.
  useEffect(() => {
    api.liveSettings().then((s) => {
      if (s.chart_default_range_secs)   setSeconds(s.chart_default_range_secs);
      if (typeof s.chart_default_refresh_ms === "number") setRefreshMs(s.chart_default_refresh_ms);
    }).catch(() => {/* keep hardcoded defaults on error */});
  }, []);
  const [view, setView] = useState<ViewMode>("total");
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [searchError, setSearchError] = useState("");
  const { settings: geoSettings } = useGeoSettings();
  const { zones } = useIPZone();

  // Bump local nonce whenever the global "Refresh all" event fires (e.g. from
  // the Dashboard Refresh button or from the ProtoChart toolbar Refresh button).
  const refreshAllNonce = useRefreshAll();
  useEffect(() => {
    if (refreshAllNonce > 0) setNonce((n) => n + 1);
  }, [refreshAllNonce]);

  // Build available view options based on which geo classes are enabled.
  const viewOptions = useMemo(() => {
    return ALL_VIEW_OPTIONS.filter((o) => {
      if (o.value === "nix") return geoSettings.nix_enabled;
      if (o.value === "ixp") return geoSettings.ixp_enabled;
      return true; // "total" is always available
    });
  }, [geoSettings.nix_enabled, geoSettings.ixp_enabled]);

  // If the currently selected view becomes unavailable, fall back to "total".
  useEffect(() => {
    const available = viewOptions.map((o) => o.value);
    if (!available.includes(view)) {
      setView("total");
    }
  }, [viewOptions, view]);

  const doSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setSearching(true);
    setNotFound(false);
    setSearchError("");
    setNodes([]);
    try {
      const resp = await api.ipSearch(trimmed);
      setQuery(resp.query);
      setNodes(resp.nodes);
      setNotFound(resp.nodes.length === 0);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : "search failed");
    } finally {
      setSearching(false);
    }
  }, []);

  // Auto-search when navigated from sidebar with ?q= param; ?view= sets initial view mode.
  // ?zone=<id> synthesises a single "whole zone" node card and skips ipSearch entirely.
  useEffect(() => {
    const q = searchParams.get("q");
    const v = searchParams.get("view");
    const zoneParam = searchParams.get("zone");
    if (v === "nix" || v === "ixp" || v === "total") setView(v as ViewMode);

    if (zoneParam) {
      const zoneID = parseInt(zoneParam, 10);
      if (Number.isFinite(zoneID)) {
        const z = zones.find((x) => x.id === zoneID);
        const label = `zone: ${z ? z.name : `#${zoneID}`}`;
        setInput(label);
        setQuery(label);
        setNotFound(false);
        setSearchError("");
        setSearching(false);
        setNodes([
          {
            id: zoneID,
            prefix: label,
            description: z ? `${z.name} — all prefixes` : "",
            match: "zone" as IPNode["match"],
          },
        ]);
      }
      return;
    }

    if (q) { setInput(q); doSearch(q); }
  }, [searchParams, doSearch, zones]);

  // Auto-refresh: bump nonce every refreshMs
  useEffect(() => {
    if (refreshMs <= 0 || nodes.length === 0) return;
    const id = window.setInterval(() => setNonce((n) => n + 1), refreshMs);
    return () => window.clearInterval(id);
  }, [refreshMs, nodes.length]);

  return (
    <div>
      {/* Search + controls bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={8} align="middle">
          <Col flex="1">
            <Input
              size="large"
              prefix={<SearchOutlined style={{ color: "var(--muted)" }} />}
              placeholder="Enter IP or CIDR — e.g.  116.118.47.215  or  116.118.47.0/24"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={() => doSearch(input)}
              allowClear
              style={{ fontFamily: "monospace" }}
            />
          </Col>
          {/* View */}
          <Col>
            <span style={{ fontSize: 12, color: "var(--muted)", marginRight: 4 }}>View</span>
            <Select
              size="large"
              value={view}
              onChange={(v) => { setView(v); setNonce((n) => n + 1); }}
              options={viewOptions}
              style={{ width: 100 }}
            />
          </Col>
          {/* Time Range */}
          <Col>
            <span style={{ fontSize: 12, color: "var(--muted)", marginRight: 4 }}>Time Range</span>
            <Select
              size="large"
              value={customRange ? CUSTOM_RANGE_VALUE : seconds}
              onChange={(v) => {
                if (v === CUSTOM_RANGE_VALUE) {
                  const now = dayjs();
                  setCustomRange([now.subtract(seconds, "second"), now]);
                } else {
                  setSeconds(v);
                  setCustomRange(null);
                  setNonce((n) => n + 1);
                }
              }}
              options={RANGES}
              style={{ width: 140 }}
            />
          </Col>
          {customRange && (
            <Col>
              <DatePicker.RangePicker
                size="large"
                showTime={{ format: "HH:mm" }}
                format="MM-DD HH:mm"
                value={customRange}
                allowClear={false}
                onChange={(v) => {
                  if (v && v[0] && v[1]) {
                    setCustomRange([v[0], v[1]]);
                    setNonce((n) => n + 1);
                  }
                }}
                presets={[
                  { label: "Last 1 h",  value: [dayjs().subtract(1, "hour"), dayjs()] },
                  { label: "Last 6 h",  value: [dayjs().subtract(6, "hour"), dayjs()] },
                  { label: "Last 12 h", value: [dayjs().subtract(12, "hour"), dayjs()] },
                  { label: "Last 24 h", value: [dayjs().subtract(24, "hour"), dayjs()] },
                  { label: "Last 3 d",  value: [dayjs().subtract(3, "day"), dayjs()] },
                  { label: "Last 7 d",  value: [dayjs().subtract(7, "day"), dayjs()] },
                  { label: "Today",     value: [dayjs().startOf("day"), dayjs()] },
                  { label: "Yesterday", value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
                ]}
                style={{ width: 290 }}
              />
            </Col>
          )}
          {/* Refresh */}
          <Col>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent(REFRESH_ALL_EVENT))}
              title="Refresh now"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "transparent", border: "none", padding: "2px 6px",
                fontSize: 12, color: "var(--cyan)", cursor: "pointer",
                fontWeight: 600, lineHeight: 1, marginRight: 4,
              }}
            >
              <ReloadOutlined style={{ fontSize: 12 }} />
              Refresh
            </button>
            <Select
              size="large"
              value={refreshMs}
              onChange={setRefreshMs}
              options={REFRESHES}
              style={{ width: 120 }}
              suffixIcon={
                <ReloadOutlined
                  style={{ cursor: "pointer", color: "var(--red)" }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent(REFRESH_ALL_EVENT));
                  }}
                />
              }
            />
          </Col>
        </Row>
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--muted)" }}>
          Press Enter to search. Accepts IPv4/IPv6 with or without prefix length.
        </div>
      </Card>

      {/* Results */}
      {searching && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Searching…" />
        </div>
      )}

      {searchError && <Alert type="error" message={searchError} style={{ marginBottom: 12 }} />}

      {notFound && (
        <Alert
          type="info"
          message={`No monitored subnet found for "${query}". Check that the IP is in a configured IP Zone.`}
          style={{ marginBottom: 12 }}
        />
      )}

      {nodes.length > 0 && (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: "var(--muted)" }}>
            {nodes.length} matching subnet{nodes.length > 1 ? "s" : ""} for{" "}
            <Text code>{query}</Text>
          </div>
          {nodes.map((n) => (
            <NodeCard key={n.id} node={n} seconds={seconds} fromTs={fromTs} toTs={toTs} nonce={nonce} view={view} />
          ))}
        </>
      )}

      {!searching && nodes.length === 0 && !notFound && !searchError && (
        <div style={{ textAlign: "center", color: "var(--muted)", padding: "60px 0", fontSize: 14 }}>
          <SearchOutlined style={{ fontSize: 48, marginBottom: 12, display: "block" }} />
          Type an IP address or subnet above and press Enter
        </div>
      )}
    </div>
  );
}
